import { Animal, AnimalBase } from "../types/interfaces";
import { updatePreniesHistorial, updatePesajes, updateUbicacionesHistorial } from "./historicalArrays";

/**
 * Normalize caravana separators consistently across the application
 * Replaces any non-alphanumeric characters with spaces and collapses multiple spaces
 * Converts to uppercase to ensure no caravans are stored in lowercase
 */
export function normalizeCaravana(caravana: string): string {
  if (!caravana) return caravana;

  // Replace slashes with empty string, then replace other non-alphanumeric and non-space characters with space
  // This standardizes separators like -, _, ., etc. to spaces but removes /
  let normalized = caravana.replace(/\//g, '').replace(/[^a-zA-Z0-9\s]/g, ' ');

  // Replace multiple consecutive spaces with a single space
  normalized = normalized.replace(/\s+/g, ' ');

  // Trim leading and trailing spaces and convert to uppercase
  return normalized.trim().toUpperCase();
}

/**
 * Helper function to normalize Spanish date formats for searching
 * Supports multiple formats: "4 oct 2025", "04-10", "04-10-2025", "4-10-2025", "10-2025",
 * Also supports slash separators: "04/10/2025", "04/10/26", "04/10"
 * And 2-digit years: "04-10-26", "04/10/26"
 * And ISO format: "2025-10-04"
 *
 * IMPORTANT: This function normalizes to DD-MM-YYYY format for comparison.
 * When the first part is > 12, it's clearly a day. When both parts are <= 12,
 * we assume DD-MM format (Argentine convention).
 */
export function normalizeSpanishDateForSearch(dateString: string): string {
  const normalized = dateString.toLowerCase().replace(/\./g, '');

  // Convert Spanish month names to numbers for consistent searching
  const monthMap: { [key: string]: string } = {
    'ene': '01', 'feb': '02', 'mar': '03', 'abr': '04',
    'may': '05', 'jun': '06', 'jul': '07', 'ago': '08',
    'sep': '09', 'oct': '10', 'nov': '11', 'dic': '12'
  };

  // Replace month names with numbers
  let result = normalized;
  for (const [month, num] of Object.entries(monthMap)) {
    result = result.replace(new RegExp(`\\b${month}\\b`, 'g'), num);
  }

  // Normalize separators: convert slashes to hyphens for consistent parsing
  result = result.replace(/\//g, '-');

  // Ensure consistent formatting: pad single digits, add missing year if needed
  const parts = result.split(/[-\s]+/).filter(p => p);

  if (parts.length === 1 && parts[0].length === 2) {
    // Month only (like "10"), add current year
    const currentYear = new Date().getFullYear().toString();
    return `${parts[0].padStart(2, '0')}-${currentYear}`;
  }

  if (parts.length >= 2) {
    const first = parts[0];
    const second = parts[1];
    let year = parts[2] || new Date().getFullYear().toString();

    // Handle ISO format (YYYY-MM-DD) - detect when first part is a 4-digit year
    if (/^\d{4}$/.test(first) && parts.length >= 3) {
      // This is YYYY-MM-DD format - convert to DD-MM-YYYY
      const isoYear = first;
      const isoMonth = second.padStart(2, '0');
      const isoDay = (parts[2] || '01').padStart(2, '0');
      return `${isoDay}-${isoMonth}-${isoYear}`;
    }

    // Handle 2-digit years: convert to 4-digit (26 -> 2026, 99 -> 1999)
    if (year.length === 2) {
      const twoDigitYear = parseInt(year, 10);
      // Assume years 00-30 are 2000-2030, 31-99 are 1931-1999
      year = twoDigitYear <= 30 ? `20${year}` : `19${year}`;
    }

    // Detect MM-YYYY format (when second part is a 4-digit year and first is <= 12)
    // E.g., "02-2026" should be month-year, not day-month
    if (parts.length === 2 && /^\d{4}$/.test(second) && parseInt(first, 10) <= 12) {
      // This is MM-YYYY format - convert to DD-MM-YYYY by using day = "XX"
      // We'll use "01" as a placeholder day since we're matching by month-year
      return `01-${first.padStart(2, '0')}-${second}`;
    }

    // Detect MM-YY format (when second part is a 2-digit number and first is <= 12)
    // After year conversion above, this handles "02-26" -> month-year
    if (parts.length === 2 && second.length === 2 && parseInt(first, 10) <= 12) {
      // Could be either DD-MM or MM-YY
      // If second > 12, it's more likely MM-YY (month-year with 2-digit year)
      const secondNum = parseInt(second, 10);
      if (secondNum > 12) {
        // This is MM-YY format
        return `01-${first.padStart(2, '0')}-${year}`;
      }
      // Otherwise assume DD-MM format (Argentine convention)
    }

    return `${first.padStart(2, '0')}-${second.padStart(2, '0')}-${year}`;
  }

  return result;
}

/**
 * Check if a search date query matches a formatted date
 * Supports various date formats and partial matches
 */
export function matchesDateQuery(searchQuery: string, formattedDate: string): boolean {
  // Handle incomplete date inputs (user is still typing)
  // Detect patterns that indicate the user is in the middle of typing a date
  const cleanSearch = searchQuery.trim().toLowerCase();
  const endsWithSeparator = /[-\/]$/.test(cleanSearch);
  const containsSeparator = /[-\/]/.test(cleanSearch);
  const looksLikeIncompleteDate = containsSeparator && cleanSearch.length < 10; // Complete dates are ~10 chars

  if (endsWithSeparator || looksLikeIncompleteDate) {
    // User is typing an incomplete date - use prefix matching
    // "23-" should match "23-02-2026", "23/02/2026", etc.
    // "26/0" should match "26/02/2026"
    const searchPrefix = cleanSearch.replace(/\//g, '-'); // Normalize to hyphens
    const normalizedDateForPrefix = formattedDate.toLowerCase().replace(/\//g, '-');
    if (normalizedDateForPrefix.startsWith(searchPrefix)) {
      return true;
    }
  }

  const normalizedSearch = normalizeSpanishDateForSearch(searchQuery);
  const normalizedDate = normalizeSpanishDateForSearch(formattedDate);

  // Exact match - only if the search is a complete date format or clear substring
  if (normalizedDate === normalizedSearch) {
    return true;
  }

  // Allow substring matches only for longer search terms (more specific searches)
  if (normalizedSearch.length > 3 && normalizedDate.includes(normalizedSearch)) {
    return true;
  }

  // Split into components
  const searchParts = normalizedSearch.split('-');
  const dateParts = normalizedDate.split('-');

  // Handle month-year searches (MM-YYYY or MM-YY)
  // The normalizeSpanishDateForSearch converts "02-2026" to "01-02-2026"
  // So we need to check if the search was originally a month-year format
  const originalSearchClean = searchQuery.replace(/\//g, '-');
  const originalParts = originalSearchClean.split('-');

  if (originalParts.length === 2 && dateParts.length === 3) {
    const [first, second] = originalParts;
    const [, dateMonth, dateYear] = dateParts;

    // Check if this is MM-YYYY format (first <= 12, second is 4-digit year)
    if (/^\d{4}$/.test(second) && parseInt(first, 10) <= 12) {
      const searchMonth = first.padStart(2, '0');
      // Month-year match (e.g., "02-2026" matches any day in February 2026)
      if (searchMonth === dateMonth && second === dateYear) {
        return true;
      }
    }

    // Check if this is MM-YY format (first <= 12, second is 2-digit year)
    if (/^\d{2}$/.test(second) && parseInt(first, 10) <= 12 && parseInt(second, 10) > 12) {
      const searchMonth = first.padStart(2, '0');
      // Convert 2-digit year to 4-digit for comparison
      const twoDigitYear = parseInt(second, 10);
      const searchYear = twoDigitYear <= 30 ? `20${second}` : `19${second}`;
      // Month-year match with 2-digit year
      if (searchMonth === dateMonth && searchYear === dateYear) {
        return true;
      }
    }

    // Day-month match (e.g., "04-10" matches October 4th of any year)
    const searchDay = first.padStart(2, '0');
    const searchMon = second.padStart(2, '0');
    const [dateD, dateM] = dateParts;

    if (searchDay === dateD && searchMon === dateM) {
      return true;
    }
  }

  if (searchParts.length === 1 && dateParts.length === 3) {
    const [searchTerm] = searchParts;
    const [, , dateYear] = dateParts;

    // Year match only for 4-digit years (e.g., "2025" matches any date in 2025)
    if (searchTerm.length === 4 && /^\d{4}$/.test(searchTerm) && searchTerm === dateYear) {
      return true;
    }
  }

  return false;
}

export function syncAnimalesLocal(
  ExistingAnimal: Animal | undefined,
  NEW: AnimalBase
): Animal | null {
  if(NEW.estado === "BORRADO"){
    return null;
  }
  // 2. Si no existe → Insert
  if (ExistingAnimal) {
    // Reactivación (reuso de caravana): si el animal existente estaba
    // soft-deleteado y entra un evento no-BORRADO, limpiamos is_deleted localmente.
    // Es el espejo del trigger sync_animales de Supabase: así una caravana
    // recargada vuelve a verse en la lista sin esperar al próximo pull.
    if ((ExistingAnimal as any).is_deleted === true) {
      (ExistingAnimal as any).is_deleted = false;
    }
    // 3. Si existe → Update condicional
    const checkUpdate = (
      field: keyof AnimalBase,
      lastEventField: keyof Animal,
      lastModField: keyof Animal
    ) => {
      // Use >= for fecha_de_creacion comparison to handle rapid successive updates
      // that may have the same timestamp or where read-modify-write races occur
      const shouldUpdate =
        NEW[field] !== null &&
        NEW[field] !== undefined &&
        (ExistingAnimal[lastEventField] === undefined ||
          ExistingAnimal[lastEventField] === null ||
          NEW.fecha_de_evento >= (ExistingAnimal[lastEventField] as string))
        && (ExistingAnimal[lastModField] === undefined ||
          ExistingAnimal[lastModField] === null ||
          NEW.fecha_de_creacion! >= (ExistingAnimal[lastModField] as string));

      if (shouldUpdate) {
        // update ExistingAnimal directly
        (ExistingAnimal as any)[field] = NEW[field];
        (ExistingAnimal as any)[lastEventField] = NEW.fecha_de_evento;
        (ExistingAnimal as any)[lastModField] = NEW.fecha_de_creacion!;
      }
    };

    if(NEW.peso !== null && NEW.peso !== undefined
       && (ExistingAnimal.fecha_de_evento_peso_inicial === null 
         || ExistingAnimal.fecha_de_evento_peso_inicial === undefined
         || ExistingAnimal.fecha_de_evento_peso_inicial >= NEW.fecha_de_evento) &&
         (ExistingAnimal.fecha_modificacion_peso_inicial === undefined ||
          ExistingAnimal.fecha_modificacion_peso_inicial === null ||
         ExistingAnimal.fecha_modificacion_peso_inicial <= NEW.fecha_de_creacion!)){
        ExistingAnimal.peso_inicial = NEW.peso;
        ExistingAnimal.fecha_de_evento_peso_inicial = NEW.fecha_de_evento;
        ExistingAnimal.fecha_modificacion_peso_inicial = NEW.fecha_de_creacion;
    }

    if(NEW.operacion_compra_id){
      ExistingAnimal.operacion_compra_id = NEW.operacion_compra_id;
      ExistingAnimal.info_compra = NEW.info_compra;
    }

    if(NEW.operacion_venta_id){
      ExistingAnimal.operacion_venta_id = NEW.operacion_venta_id;
      ExistingAnimal.info_venta = NEW.info_venta;
    }

    // fecha_nacimiento is a simple property (not event-based) - just copy if provided
    if(NEW.fecha_nacimiento){
      ExistingAnimal.fecha_nacimiento = NEW.fecha_nacimiento;
    }

    // madre/padre (parentesco) are simple properties (not event-based) - just copy if provided
    if(NEW.madre){
      ExistingAnimal.madre = NEW.madre;
    }

    if(NEW.padre){
      ExistingAnimal.padre = NEW.padre;
    }

    checkUpdate("peso", "fecha_ultimo_evento_peso", "fecha_ultima_modificacion_peso");
    checkUpdate("sexo", "fecha_ultimo_evento_sexo", "fecha_ultima_modificacion_sexo");
    checkUpdate("ubicacion_id", "fecha_ultimo_evento_ubicacion_id", "fecha_ultima_modificacion_ubicacion_id");
    checkUpdate("raza", "fecha_ultimo_evento_raza", "fecha_ultima_modificacion_raza");
    checkUpdate("observaciones", "fecha_ultimo_evento_observaciones", "fecha_ultima_modificacion_observaciones");
    checkUpdate("categoria", "fecha_ultimo_evento_categoria", "fecha_ultima_modificacion_categoria");
    checkUpdate("prenies", "fecha_ultimo_evento_prenies", "fecha_ultima_modificacion_prenies");
    checkUpdate("edad", "fecha_ultimo_evento_edad", "fecha_ultima_modificacion_edad");
    checkUpdate("estado", "fecha_ultimo_evento_estado", "fecha_ultima_modificacion_estado");
    checkUpdate("tiene_ternero", "fecha_ultimo_evento_tiene_ternero", "fecha_ultima_modificacion_tiene_ternero");
    checkUpdate("caravanas_viejas", "fecha_ultimo_evento_caravanas_viejas", "fecha_ultima_modificacion_caravanas_viejas");
    checkUpdate("es_comprado", "fecha_ultimo_evento_es_comprado", "fecha_ultima_modificacion_es_comprado");
    checkUpdate("renspa", "fecha_ultimo_evento_renspa", "fecha_ultima_modificacion_renspa");

    // Update historical arrays for pregnancy, weight, and ubicacion
    const updatedPreniesHistorial = updatePreniesHistorial(ExistingAnimal, NEW);
    if (updatedPreniesHistorial) {
      ExistingAnimal.prenies_historial = updatedPreniesHistorial;
    }

    const updatedPesajes = updatePesajes(ExistingAnimal, NEW);
    if (updatedPesajes) {
      ExistingAnimal.pesajes = updatedPesajes;
    }

    const updatedUbicaciones = updateUbicacionesHistorial(ExistingAnimal, NEW);
    if (updatedUbicaciones) {
      ExistingAnimal.ubicaciones_historial = updatedUbicaciones;
    }

    // siempre actualizar fecha_de_evento si es mayor
    ExistingAnimal.fecha_de_evento =
      NEW.fecha_de_evento > ExistingAnimal.fecha_de_evento
        ? NEW.fecha_de_evento
        : ExistingAnimal.fecha_de_evento;

    return ExistingAnimal;

  }
  else{
    const insertData: Animal = {
      ...NEW,

      // ÚLTIMA MODIFICACIÓN
      fecha_ultima_modificacion_sexo: NEW.sexo == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_peso: NEW.peso == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_ubicacion_id: NEW.ubicacion_id == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_raza: NEW.raza == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_observaciones: NEW.observaciones == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_categoria: NEW.categoria == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_prenies: NEW.prenies == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_edad: NEW.edad == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_estado: NEW.estado == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_tiene_ternero: NEW.tiene_ternero == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_caravanas_viejas: NEW.caravanas_viejas == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_es_comprado: NEW.es_comprado == null ? undefined : NEW.fecha_de_creacion,
      fecha_ultima_modificacion_renspa: NEW.renspa == null ? undefined : NEW.fecha_de_creacion,

      // PESO INICIAL – depende de NEW.peso
      fecha_modificacion_peso_inicial: NEW.peso == null ? undefined : NEW.fecha_de_creacion,

      // EVENTOS
      fecha_ultimo_evento_sexo: NEW.sexo == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_peso: NEW.peso == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_ubicacion_id: NEW.ubicacion_id == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_raza: NEW.raza == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_observaciones: NEW.observaciones == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_categoria: NEW.categoria == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_prenies: NEW.prenies == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_edad: NEW.edad == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_estado: NEW.estado == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_tiene_ternero: NEW.tiene_ternero == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_caravanas_viejas: NEW.caravanas_viejas == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_es_comprado: NEW.es_comprado == null ? undefined : NEW.fecha_de_evento,
      fecha_ultimo_evento_renspa: NEW.renspa == null ? undefined : NEW.fecha_de_evento,

      // PESO INICIAL – depende de NEW.peso
      fecha_de_evento_peso_inicial: NEW.peso == null ? undefined : NEW.fecha_de_evento,
      peso_inicial: NEW.peso,

      // Initialize historical arrays
      prenies_historial: undefined,
      pesajes: undefined,
      ubicaciones_historial: undefined
    };

    // Populate historical arrays if data exists
    const updatedPreniesHistorial = updatePreniesHistorial(undefined, NEW);
    if (updatedPreniesHistorial) {
      insertData.prenies_historial = updatedPreniesHistorial;
    }

    const updatedPesajes = updatePesajes(undefined, NEW);
    if (updatedPesajes) {
      insertData.pesajes = updatedPesajes;
    }

    const updatedUbicaciones = updateUbicacionesHistorial(undefined, NEW);
    if (updatedUbicaciones) {
      insertData.ubicaciones_historial = updatedUbicaciones;
    }

    return insertData;
  }
}
